import Tilt from 'react-parallax-tilt';

import * as api from '@gitbook/api-client';
import { useViewport } from '@gitbook/util-react-hooks';

import { isNewPricingInputs, type StepId, type StepperInputs, type StepperType } from '../common';
import { GridTexture } from './GridTexture';
import { SiteTemplate } from './SiteTemplate';

export const PREVIEW_HEIGHT = 579;
export const PREVIEW_WIDTH = 1079;
export const PREVIEW_PADDING = 40;
export const MARGIN = 128;

const height = PREVIEW_HEIGHT + PREVIEW_PADDING * 2;
const width = PREVIEW_WIDTH + PREVIEW_PADDING * 2;

export function Preview<T extends StepperType>(props: {
    stepperType: T;
    inputs: StepperInputs<T>;
    step: StepId<T>;
}) {
    const { stepperType, inputs, step } = props;
    const viewport = useViewport();

    const shouldShowRightSide =
        step === 'features' &&
        isNewPricingInputs(stepperType, inputs) &&
        inputs.features.plan === api.SiteType.Sponsored;

    return (
        <Tilt
            scale={1.001}
            transitionSpeed={2500}
            tiltMaxAngleX={6}
            tiltMaxAngleY={6}
            className="absolute flex"
            style={{
                padding: PREVIEW_PADDING,
                backfaceVisibility: 'hidden',
                transformStyle: 'preserve-3d',
                height,
                width,
                // Center vertically with at least top and left margin
                top: Math.max(MARGIN, (viewport.height - height) / 2),
                left:
                    Math.max(MARGIN, (viewport.width / 2 - width) / 2) -
                    (shouldShowRightSide ? width / 2 + MARGIN : 0),
            }}
        >
            <div
                className="absolute inset-0"
                style={{
                    margin: PREVIEW_PADDING / 2,
                    backgroundImage:
                        'linear-gradient(to right, rgba(255, 255, 255, 0.4), rgba(255, 255, 255, 0))',
                }}
            />
            <div
                className="shimmer absolute inset-0"
                style={{
                    margin: PREVIEW_PADDING / 2,
                    backgroundImage:
                        'linear-gradient(to right, rgba(255, 255, 255, 0), rgba(255, 255, 255, 0.75) 50%, rgba(255, 255, 255, 0) 100%)',
                    animation: 'shimmer 9s linear infinite',
                }}
            />
            <GridTexture
                className="absolute inset-0 flex rounded-lg"
                style={{ padding: PREVIEW_PADDING }}
            />
            <div
                className="flex"
                style={{
                    transform: 'translateZ(40px) scale(0.96)',
                }}
            >
                <SiteTemplate height={PREVIEW_HEIGHT} className="relative" {...props} />
            </div>
        </Tilt>
    );
}
